import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

/**
 * Sprint K · Legal Analysis first-run disclaimer modal.
 *
 * Opens the first time a session-scoped tenant lands on any /app/legal/analysis
 * route. Owner (`user.role === "platform_owner"`) is bypassed entirely per
 * K-8. Non-owner must check the acknowledgment box and click Continue before
 * the underlying page renders.
 *
 * The ack is cached per (tenant_id, session) — refreshing the tab or logging
 * out clears it.
 */

const SESSION_KEY_PREFIX = "nexus_legal_disclaimer_ack_";

export default function LegalDisclaimerModal({ tenantId, onAcknowledge }) {
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        if (isOwner) {
            onAcknowledge?.();
            return;
        }
        if (!tenantId) return;
        const key = SESSION_KEY_PREFIX + tenantId;
        const acked = sessionStorage.getItem(key);
        if (acked === "1") {
            onAcknowledge?.();
        } else {
            setOpen(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwner, tenantId]);

    const confirm = () => {
        if (!checked) return;
        const key = SESSION_KEY_PREFIX + tenantId;
        sessionStorage.setItem(key, "1");
        setOpen(false);
        onAcknowledge?.();
    };

    if (isOwner || !open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: "rgba(4, 6, 10, 0.72)", backdropFilter: "blur(6px)" }}
            data-testid="legal-disclaimer-modal"
        >
            <div
                className="w-full max-w-lg rounded-xl p-6"
                style={{
                    background: "var(--atlas-bg-elevated)",
                    border: "1px solid var(--atlas-border-strong)",
                    boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
                }}
            >
                <div className="flex items-start gap-3">
                    <div
                        className="w-10 h-10 rounded-lg grid place-items-center flex-shrink-0"
                        style={{ background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.35)" }}
                    >
                        <AlertTriangle className="w-5 h-5" style={{ color: "#fbbf24" }} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2
                            className="text-lg font-semibold"
                            style={{ color: "var(--atlas-text-primary)" }}
                        >
                            AI-Generated Legal Information — Not Legal Advice
                        </h2>
                        <p
                            className="mt-2 text-sm leading-relaxed"
                            style={{ color: "var(--atlas-text-secondary)" }}
                        >
                            Every output from Legal Analysis is generated by Nexus One AI Workforce
                            Members. It reflects publicly available law and does <strong>not</strong>{" "}
                            constitute legal advice. All outputs must be reviewed by a licensed
                            attorney admitted to practice in your jurisdiction before any client
                            reliance. Nexus One is not a law firm. No attorney-client relationship
                            is created by use of this platform.
                        </p>
                    </div>
                </div>

                <label
                    className="mt-5 flex items-start gap-3 cursor-pointer p-3 rounded-lg"
                    style={{
                        background: "var(--atlas-bg-deep)",
                        border: "1px solid var(--atlas-border-soft)",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        className="mt-0.5 w-4 h-4"
                        data-testid="legal-disclaimer-checkbox"
                    />
                    <span
                        className="text-sm"
                        style={{ color: "var(--atlas-text-primary)" }}
                    >
                        I understand and acknowledge. I will not rely on any Legal Analysis output
                        without independent review by a licensed attorney.
                    </span>
                </label>

                <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="px-4 py-2 rounded-lg text-sm transition"
                        style={{
                            border: "1px solid var(--atlas-border-strong)",
                            color: "var(--atlas-text-secondary)",
                            background: "transparent",
                        }}
                        data-testid="legal-disclaimer-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={!checked}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            background: checked ? "var(--atlas-primary)" : "var(--atlas-bg-muted)",
                            color: checked ? "white" : "var(--atlas-text-tertiary)",
                            border: "1px solid " + (checked ? "var(--atlas-primary)" : "var(--atlas-border-strong)"),
                        }}
                        data-testid="legal-disclaimer-ack"
                    >
                        Continue to Legal Analysis
                    </button>
                </div>
            </div>
        </div>
    );
}
